/**
 * Org × UTC hour usage rollup (router#2937 item 2): the fold route the
 * control-loops `usage_org_hourly_fold` handler drives, and the constants both
 * sides and the gateway readers share.
 *
 * `POST /internal/usage-org-hourly/fold` (Tokyo gateway only; CN refuses):
 * one bounded pass that seals at most {@link USAGE_ORG_HOURLY_MAX_HOURS_PER_FOLD}
 * hours, re-verifies at most as many, runs the hourly reader shadow when it is
 * due, and applies retention. Every write is region-local derived state
 * (gateway 0110); nothing here touches money of record.
 */

export const USAGE_ORG_HOURLY_FOLD_PATH = '/internal/usage-org-hourly/fold';

/** New hours sealed per fold call, oldest first. */
export const USAGE_ORG_HOURLY_MAX_HOURS_PER_FOLD = 4;
/** Hours still waiting for verdicts refolded per fold call (least recently folded first). */
export const USAGE_ORG_HOURLY_MAX_PENDING_REFOLDS = 1;
/**
 * Served hours whose unit snapshot is still missing (`units_folded_at` NULL),
 * folded per call after the token work, newest first, and only while the
 * call's budget remains.
 */
export const USAGE_ORG_HOURLY_MAX_UNIT_BACKFILL_HOURS = 4;
/** Hours due their +26 h verify, per fold call. */
export const USAGE_ORG_HOURLY_MAX_VERIFY_HOURS = 4;
/**
 * Hours of the served range rechecked per fold call by the rolling recheck
 * (a cursor walking [served_from, served_through) and wrapping). Most hours
 * cost one index-only fingerprint (count and max created_at); only a changed
 * fingerprint or an hour served with pending verdicts is recomputed. 24 a call
 * at 12 calls an hour walks 92 days in about 8 hours.
 */
export const USAGE_ORG_HOURLY_RECHECK_HOURS = 24;
/**
 * The reader shadow, when due, runs first in the call, capped at this much of
 * the budget, so verifies and rechecks never starve the readers' evidence.
 */
export const USAGE_ORG_HOURLY_READER_SHADOW_BUDGET_MS = 12_000;
/**
 * An hour still waiting for verdicts this long after it ended is served as it
 * stands: an unrated `rating_pending` row reads 0 money in the original SQL
 * as well, and a verdict landing later is replaced by the rolling recheck.
 */
export const USAGE_ORG_HOURLY_PENDING_ACCEPT_HOURS = 24;
/**
 * An hour H is sealed once H + 1h + this ≤ now — the usage rollup's own rating
 * lag (`RATING_ROLLUP_LAG_HOURS`): production rating p99.9 is 99 s, the worst
 * seen about 12 minutes.
 */
export const USAGE_ORG_HOURLY_SEAL_LAG_HOURS = 2;
/** A sealed hour is recomputed and compared once H + 1h + this ≤ now. */
export const USAGE_ORG_HOURLY_VERIFY_LAG_HOURS = 26;
/** The first fold backfills from date_trunc('hour', now − this). */
export const USAGE_ORG_HOURLY_BACKFILL_DAYS = 85;
/** Rollup rows and hour rows older than now − this are deleted. */
export const USAGE_ORG_HOURLY_RETENTION_DAYS = 92;
/**
 * Hours whose hour × day-dimension rows (`usage_dim_hourly`) the backfill writes per call when the
 * job knob does not say.
 */
export const USAGE_ORG_HOURLY_MAX_DIM_BACKFILL_HOURS = 4;
/** Shadow-run rows older than now − this are deleted. */
export const USAGE_ORG_HOURLY_SHADOW_RETENTION_DAYS = 30;
/**
 * How long a proved hour stays servable after its last verify or recheck,
 * and how long a reader-shadow mismatch keeps every request reader on the
 * original SQL. A shadow run does not arm an org: each hour is proved on
 * its own row (`verified_at`, `pending_rows`, `unmatched_rated_rows`,
 * `rechecked_at`, and a fingerprint that still matches `usage_logs`).
 */
export const USAGE_ORG_HOURLY_READ_HOLD_HOURS = 48;
/**
 * Kept so a doubled cron delivery cannot count one clean comparison twice.
 * It is not a read gate. `recordReaderShadow` drops a **complete** clean run
 * that arrives within USAGE_ORG_HOURLY_READER_SHADOW_INTERVAL_MINUTES (55)
 * of a complete clean run already recorded. An incomplete clean run is
 * recorded and does not move `last_reader_shadow_at`, so it does not push
 * the next comparison out by 55 minutes. A reader-shadow mismatch (not a
 * verify replacement) closes request readers for
 * USAGE_ORG_HOURLY_READ_HOLD_HOURS.
 */
export const USAGE_ORG_HOURLY_READ_MIN_CLEAN_RUNS = 1;
export const USAGE_ORG_HOURLY_READ_FOLD_FRESH_MINUTES = 15;
/** The gateway's own wall-clock budget for one fold call. */
export const USAGE_ORG_HOURLY_FOLD_BUDGET_MS = 20_000;
/** Below this much tick time left the handler yields instead of calling. */
export const USAGE_ORG_HOURLY_FOLD_MIN_BUDGET_MS = 3_000;
/**
 * Hard cap the fold route applies to a finite `budgetMs`. Above this the
 * route clamps; a non-finite value is treated as absent.
 *
 * Control-loops fetch slack is 5000. The handler kill is budget + slack +
 * 5000, and `timeoutMsFor` uses that same sum when a budget knob is in
 * force (no knob: the compiled 30000). The invariant is the fetch timeout
 * (`budgetMs + 5000`) stays under that kill: 45000 + 5000 = 50000 < 55000.
 *
 * A Cloudflare scheduled handler's wall is 15 minutes. What binds 45000 is
 * the 1-minute cron (`TICK_PERIOD_MS` = 60000): 55000 still finishes inside
 * the minute. A larger budget would overlap the next minute, and the overlap
 * only comes back as `skipped: 'locked'` from the fold advisory lock, so the
 * cap is not the 15-minute wall.
 */
export const USAGE_ORG_HOURLY_FOLD_BUDGET_MAX_MS = 45_000;
/**
 * Hard cap for `maxHours` and `unitBackfillHours` on the fold route.
 * An integer outside 1..this is dropped and the function keeps its constant.
 */
export const USAGE_ORG_HOURLY_FOLD_MAX_HOURS_CAP = 30;
/** The uplink apply worker's last report must be younger than this (defence in depth). */
export const USAGE_ORG_HOURLY_UPLINK_MAX_LAG_SECONDS = 120;
/**
 * An hour is sealed, verified or rechecked only once it ended at least this
 * long before the uplink watermark (the newest CN `region_readiness`
 * `checked_at` on Tokyo): CN transactions open across the watermark, and
 * clock skew between the two databases.
 */
export const USAGE_ORG_HOURLY_UPLINK_WATERMARK_MARGIN_MINUTES = 5;
/** A watermark further behind now than this counts as a stall. */
export const USAGE_ORG_HOURLY_UPLINK_WATERMARK_STALL_MINUTES = 30;
/** …and the uplink must have stayed open this long before anything is sealed. */
export const USAGE_ORG_HOURLY_UPLINK_SETTLE_MINUTES = 30;
/** served_through this far behind the newest sealable hour logs an error. */
export const USAGE_ORG_HOURLY_STALL_HOURS = 6;
/** The reader shadow runs at most once per this many minutes. */
export const USAGE_ORG_HOURLY_READER_SHADOW_INTERVAL_MINUTES = 55;
/** The reader shadow compares at most this many orgs (most rows in 24 h first). */
export const USAGE_ORG_HOURLY_READER_SHADOW_MAX_ORGS = 20;
/** Mismatch details kept in one shadow-run row. */
export const USAGE_ORG_HOURLY_SHADOW_DETAIL_LIMIT = 20;

export interface UsageOrgHourlyFoldRequest {
  /**
   * Wall clock this call may spend, ms. Absent = {@link USAGE_ORG_HOURLY_FOLD_BUDGET_MS}.
   * The route clamps a finite number to {@link USAGE_ORG_HOURLY_FOLD_BUDGET_MAX_MS}
   * and treats a non-finite number as absent.
   */
  readonly budgetMs?: number;
  /**
   * New hours sealed this call. Absent = {@link USAGE_ORG_HOURLY_MAX_HOURS_PER_FOLD}.
   * The route drops an integer outside 1..{@link USAGE_ORG_HOURLY_FOLD_MAX_HOURS_CAP}.
   */
  readonly maxHours?: number;
  /**
   * Served hours whose unit snapshot is backfilled this call. Absent =
   * {@link USAGE_ORG_HOURLY_MAX_UNIT_BACKFILL_HOURS}. Same route bound as
   * `maxHours`. Control-loops sends this equal to `maxHours` when that knob
   * is not the default.
   */
  readonly unitBackfillHours?: number;
  /**
   * Hours of the rolling recheck this call. Absent = {@link USAGE_ORG_HOURLY_RECHECK_HOURS}.
   * The route drops an integer outside 0..that constant.
   */
  readonly recheckHours?: number;
  /**
   * Hours whose dimension rows are backfilled this call. Absent =
   * {@link USAGE_ORG_HOURLY_MAX_DIM_BACKFILL_HOURS}. The route drops an
   * integer outside 1..{@link USAGE_ORG_HOURLY_FOLD_MAX_HOURS_CAP}.
   */
  readonly dimHours?: number;
  /**
   * Hours verified this call. Absent = {@link USAGE_ORG_HOURLY_MAX_VERIFY_HOURS}.
   * The route drops an integer outside 1..{@link USAGE_ORG_HOURLY_FOLD_MAX_HOURS_CAP}.
   */
  readonly verifyHours?: number;
}

/**
 * The uplink gate: `none` = this database publishes no `ndt_downlink` (a
 * single-region database: open, no wait); `closed` = it does, and there is no
 * CN watermark yet, no `ndt_uplink` subscription, the apply worker is not
 * running, or its last report is too old; `settling` = open for less than
 * USAGE_ORG_HOURLY_UPLINK_SETTLE_MINUTES; `open` (hours up to the watermark
 * minus its margin).
 */
export type UsageOrgHourlyUplinkGate = 'none' | 'settling' | 'open' | 'closed';

export interface UsageOrgHourlyReaderShadowSummary {
  readonly orgsChecked: number;
  readonly mismatches: number;
  /**
   * False when the run stopped early (budget, timeout, error). The orgs in
   * `detail.orgs` were still fully compared and stay evidence when the run
   * is otherwise clean; `complete` is the batch, not the org.
   */
  readonly complete: boolean;
  /** Sealed hours of the mismatching orgs' windows sent back to the verify queue. */
  readonly hoursReset?: number;
  /**
   * False when a clean run was not recorded because another call recorded a
   * reader run inside the last USAGE_ORG_HOURLY_READER_SHADOW_INTERVAL_MINUTES
   * (a doubled cron delivery must not count twice). Mismatches are always recorded.
   */
  readonly recorded?: boolean;
  readonly error?: string;
}

/**
 * Why a verify or recheck replaced an hour: `late_rows` = the hour gained
 * usage rows after it was folded; `late_verdict` = a pending row got its
 * rating verdict (an accepted pending hour, typically) — a legitimate change
 * that still costs the readers their evidence for 48 h; `other` = anything
 * else (should not happen — investigate).
 */
export type UsageOrgHourlyReplacementReason = 'late_rows' | 'late_verdict' | 'other';

export interface UsageOrgHourlyReplacement {
  readonly hour: string;
  readonly via: 'verify' | 'recheck';
  readonly reason: UsageOrgHourlyReplacementReason;
}

export interface UsageOrgHourlyRecheckSummary {
  /** Hours whose fingerprint matched (unchanged, nothing recomputed). */
  readonly fingerprintOnly: number;
  /** Hours fully recomputed (fingerprint changed, missing, or pending accepted). */
  readonly recomputed: number;
  readonly cursor: string | null;
  readonly roundStartedAt: string | null;
  /** When the cursor last walked the whole served range. */
  readonly lastRoundCompletedAt: string | null;
}

export interface UsageOrgHourlyFoldResponse {
  readonly ok: boolean;
  /** `locked`: another fold holds the advisory lock; `refused_cn`: CN gateway. */
  readonly skipped?: 'locked' | 'refused_cn';
  readonly error?: string;
  readonly uplink: UsageOrgHourlyUplinkGate | null;
  /** The uplink watermark (ISO) or null (none read, or no CN report yet). */
  readonly uplinkWatermark: string | null;
  /** Hour starts (ISO) folded this call, oldest first. */
  readonly sealed: readonly string[];
  /** Of `sealed`, the hours still waiting for a rating verdict. */
  readonly pendingHours: readonly string[];
  /** Of `sealed`, pending hours past USAGE_ORG_HOURLY_PENDING_ACCEPT_HOURS, served as they stand. */
  readonly pendingAccepted: readonly string[];
  readonly verifiedHours: number;
  /** Hours the rolling recheck compared this call. */
  readonly recheckedHours: number;
  /** Hours whose recompute disagreed with the stored rollup and were replaced. */
  readonly replacedHours: readonly string[];
  /** The same, with how they were found and why they changed. */
  readonly replacements: readonly UsageOrgHourlyReplacement[];
  readonly recheck: UsageOrgHourlyRecheckSummary;
  readonly readerShadow: UsageOrgHourlyReaderShadowSummary | null;
  readonly servedFrom: string | null;
  readonly servedThrough: string | null;
  readonly shadowCleanSince: string | null;
  /**
   * served_through more than USAGE_ORG_HOURLY_STALL_HOURS behind for a pending
   * hour or the uplink gate, or the uplink watermark more than
   * USAGE_ORG_HOURLY_UPLINK_WATERMARK_STALL_MINUTES behind now.
   */
  readonly stalled: boolean;
  /**
   * Served hours that had no unit snapshot and were folded this call
   * (newest first, at most USAGE_ORG_HOURLY_MAX_UNIT_BACKFILL_HOURS, and
   * only inside the budget left after the token work).
   */
  readonly unitsBackfilled: number;
  readonly retention: {
    readonly rollupRows: number;
    readonly hours: number;
    readonly unitRows: number;
    readonly shadowRuns: number;
  };
  readonly budgetExhausted: boolean;
  readonly elapsedMs: number;
  /** The dimension layer's part of this call (the layer has no arming switch). */
  readonly dim?: UsageOrgHourlyDimSummary;
}

/** The dimension layer's account of one call (gateway 0140). */
export interface UsageOrgHourlyDimSummary {
  /** Hour starts (ISO) whose dimension rows were backfilled and stamped this call, newest first. */
  readonly backfilled: readonly string[];
  /** Hours whose rows could not be read here (org rows exist, the source returned none): left unstamped, parked for an hour. */
  readonly parked: readonly string[];
  /** Hours still without current dimension rows after this call (parked ones included); null when the step did not run. */
  readonly pending: number | null;
  /** `pending` over this call's cap: the calls left until the layer is caught up (one call per interval). */
  readonly callsToCatchUp: number | null;
}

/**
 * The operator-driven historical backfill of the hour × day-dimension rows
 * (`POST /internal/usage-org-hourly/dim-backfill`, gateway 0140).
 *
 * The fold job's own `dim_hours` step is newest-first and runs after the hot
 * work in a 20 s budget, so it keeps the hours the seal writes and never
 * advances into a hole older than the layer's armed day (measured on
 * production 2026-10-24: 262 h of coverage flat at `dim_hours=30`,
 * `interval_minutes=1`, `budget_ms=45000`). This route is the other direction:
 * an explicit `[from, to)` range, OLDEST hour first, its own budget, and a
 * `nextIso` a caller loops on until the range is done. It reuses the fold's own
 * `replaceHourDim` (delete + insert + stamp), so it is idempotent and safe to
 * repeat: a stamped hour is skipped, a failed one is left unstamped (parked).
 */
export interface UsageOrgHourlyDimBackfillRequest {
  /** First hour of the range, inclusive. Any instant: floored to its hour. */
  readonly from: string;
  /**
   * End of the range, exclusive. Any instant: ceiled to its hour.
   *
   * Pass at least the `served_through` of `usage_org_hourly_state`. The reader
   * gates its table range on the NEWEST hour of the served range that has no
   * current dimension rows (`dimHourlyServingStateOf`): one such hour before
   * `served_through` moves the table range's start past it, and a start at or
   * after `served_through` returns no range at all, so the whole window falls
   * back to the live statement. A `to` short of `served_through` is answered
   * with `warning` naming the instant to pass instead.
   */
  readonly to: string;
  /**
   * Hours folded by this call at most, oldest first. Default
   * {@link USAGE_ORG_HOURLY_DIM_BACKFILL_DEFAULT_HOURS}; a present value must be
   * an integer >= 1 (anything else is a 400, not a silent default) and is
   * capped at {@link USAGE_ORG_HOURLY_DIM_BACKFILL_MAX_HOURS}.
   */
  readonly hours?: number;
  /**
   * Wall clock this call may spend, ms. Absent defaults to, and a larger
   * finite value clamps to, {@link USAGE_ORG_HOURLY_DIM_BACKFILL_BUDGET_MAX_MS}.
   * `0` is legal and is a probe: the call still returns `pendingBefore` and
   * `nextIso`, and it starts no hour (a step needs 500 ms, so `1` is the same
   * probe). A present value that is not a finite number >= 0 is a 400. The
   * call stops before starting an hour the remaining budget cannot cover.
   * Do not send a value above the cap and do not run two calls at once: the
   * cap is the bound, and a second call only contends for the fold's lock.
   */
  readonly budgetMs?: number;
}

/** One call's account. `ok: false` carries `error` and the fields it did reach. */
export interface UsageOrgHourlyDimBackfillResponse {
  readonly ok: boolean;
  /** The range this call was asked for, after the hour floor / ceil. */
  readonly from: string;
  readonly to: string;
  /** Hours inside `[from, to)` without current dimension rows before this call. */
  readonly pendingBefore: number;
  /** Hours folded and stamped by this call, oldest first. */
  readonly folded: readonly string[];
  /** Hours whose rows could not be read here (org rows exist, the source returned none): left unstamped, parked for an hour. */
  readonly parked: readonly string[];
  /** Dimension rows written by this call. */
  readonly rows: number;
  /**
   * Hours still without current dimension rows inside `[from, to)` after this
   * call, PARKED ONES INCLUDED (`pendingAfter - parkedInRange` is the fillable
   * remainder).
   */
  readonly pendingAfter: number;
  /**
   * Hours inside `[from, to)` that are parked: their org rows exist but the
   * source scan returned nothing (a day that left the live table), so this
   * route can never stamp them. `pendingAfter` stays at least this large
   * forever; that is why a driver loops on `nextIso`, not on `pendingAfter`.
   */
  readonly parkedInRange: number;
  /**
   * The earliest hour still pending inside `[from, to)` that is NOT parked, or
   * null when nothing in the range is left to fill. A caller resumes by
   * sending `{ from: nextIso, to }` and STOPS at null — {@link parkedInRange}
   * explains a `pendingAfter` that never reaches zero.
   */
  readonly nextIso: string | null;
  /** True when the call ran out of its budget or its `hours` batch; the next call continues at `nextIso`. */
  readonly budgetExhausted: boolean;
  /** True when a running fold held the org-hourly advisory lock; nothing was consumed and `nextIso` is unchanged. */
  readonly locked: boolean;
  readonly elapsedMs: number;
  /**
   * A range that cannot serve its purpose: `to` before the served range's end
   * (see {@link UsageOrgHourlyDimBackfillRequest.to}). The call still folds
   * what it was given.
   */
  readonly warning?: string;
  readonly error?: string;
}

/** Hours a single backfill call folds when the request does not say. */
export const USAGE_ORG_HOURLY_DIM_BACKFILL_DEFAULT_HOURS = 48;
/** Hard cap for one backfill call's `hours`: an integer above it is clamped. */
export const USAGE_ORG_HOURLY_DIM_BACKFILL_MAX_HOURS = 500;
/** Hard cap for one backfill call's `budgetMs`. */
export const USAGE_ORG_HOURLY_DIM_BACKFILL_BUDGET_MAX_MS = 60_000;
/** The widest `[from, to)` one request may name (a full 92-day retention window). */
export const USAGE_ORG_HOURLY_DIM_BACKFILL_MAX_SPAN_HOURS = 24 * 92;
